import { useMemo } from "react"

import { Trans, useTranslation } from "react-i18next"

import type { RequirementMatrixItem, RiskItem } from "../../types"
import type { TenderConflict } from "../conflict-alert"

import {
  AlertTriangle,
  CheckCircle,
  Info,
  MinusCircle,
  ShieldAlert,
  XCircle,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { ConflictAlert } from "../conflict-alert"
import { EvidenceCell } from "../evidence-cell"
import { TenderAiDisclaimer } from "../tender-ai-disclaimer"

const REQ_STATUS_STYLE = {
  uppfyllt: {
    icon: CheckCircle,
    badgeClass: "bg-success/10 text-success border-success/30",
    borderClass: "border-l-success",
    bgClass: "",
  },
  delvis: {
    icon: MinusCircle,
    badgeClass: "bg-warning/10 text-warning-foreground border-warning",
    borderClass: "border-l-warning",
    bgClass: "",
  },
  ej_uppfyllt: {
    icon: XCircle,
    badgeClass: "bg-destructive/10 text-destructive border-destructive",
    borderClass: "border-l-destructive",
    bgClass: "bg-destructive/[0.02]",
  },
  obekraftat: {
    icon: AlertTriangle,
    badgeClass: "bg-muted/60 text-muted-foreground border-border",
    borderClass: "border-l-border",
    bgClass: "",
  },
} as const

type ReqStatusKey = keyof typeof REQ_STATUS_STYLE

function riskScore(item: RiskItem) {
  return item.severity * item.probability
}

function riskScoreLabel(score: number, t: (key: string) => string): string {
  if (score >= 16) return t("tender.sections.analysis.riskLevel.high")
  if (score >= 9) return t("tender.sections.analysis.riskLevel.medium")
  return t("tender.sections.analysis.riskLevel.low")
}

function badgeClassToTextColor(badgeClass: string): string {
  if (badgeClass.includes("success")) return "text-success"
  if (badgeClass.includes("warning")) return "text-warning"
  if (badgeClass.includes("destructive")) return "text-destructive"
  return "text-muted-foreground"
}

function riskScoreColor(score: number): string {
  if (score >= 16) return "bg-destructive text-destructive-foreground border border-foreground"
  if (score >= 9) return "bg-warning text-warning-foreground border border-foreground"
  return "bg-success text-success-foreground border border-foreground"
}

function RiskScoreCell({ severity, probability }: { severity: number; probability: number }) {
  const score = severity * probability
  const colorClass = riskScoreColor(score)

  return (
    <span
      className={cn(
        "inline-flex size-8 items-center justify-center rounded-lg text-sm font-bold shadow-sm",
        colorClass
      )}
    >
      {score}
    </span>
  )
}

function RiskMatrisInfo() {
  const { t } = useTranslation()

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          aria-label={t("tender.sections.analysis.riskPopover.ariaLabel")}
          className="text-muted-foreground hover:text-foreground ml-1.5 size-7 rounded p-0"
          size="icon"
          type="button"
          variant="ghost"
        >
          <Info className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-4" side="bottom">
        <div className="space-y-3">
          <p className="text-sm font-semibold">{t("tender.sections.analysis.riskPopover.title")}</p>

          <div className="bg-muted/50 rounded-lg px-3 py-2 text-center text-xs font-medium">
            {t("tender.sections.analysis.riskPopover.formula")}
          </div>

          <p className="text-muted-foreground text-xs leading-relaxed">
            {t("tender.sections.analysis.riskPopover.explanation")}
          </p>

          <div className="space-y-1.5">
            <p className="text-xs font-medium">
              {t("tender.sections.analysis.riskPopover.classificationsTitle")}
            </p>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="bg-success-ink inline-block size-2.5 shrink-0 rounded-sm" />
                <span className="font-medium">{t("tender.sections.analysis.riskLevel.low")}</span>
                <span className="text-muted-foreground">
                  {t("tender.sections.analysis.riskRange.low")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-warning inline-block size-2.5 shrink-0 rounded-sm" />
                <span className="font-medium">
                  {t("tender.sections.analysis.riskLevel.medium")}
                </span>
                <span className="text-muted-foreground">
                  {t("tender.sections.analysis.riskRange.medium")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-destructive inline-block size-2.5 shrink-0 rounded-sm" />
                <span className="font-medium">{t("tender.sections.analysis.riskLevel.high")}</span>
                <span className="text-muted-foreground">
                  {t("tender.sections.analysis.riskRange.high")}
                </span>
              </div>
            </div>
          </div>

          {/* eslint-disable jsx-a11y/anchor-has-content -- Trans injects link labels from i18n (<milStd>…</milStd>). */}
          <p className="text-muted-foreground text-xs leading-relaxed">
            <Trans
              i18nKey="tender.sections.analysis.riskPopover.methodology"
              components={{
                milStd: (
                  <a
                    className="text-foreground underline underline-offset-2"
                    href="https://acqnotes.com/acqnote/tasks/risk-reporting-matrix"
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
                iso31000: (
                  <a
                    className="text-foreground underline underline-offset-2"
                    href="https://www.iso.org/standard/65694.html"
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
                safetyCulture: (
                  <a
                    className="text-foreground underline underline-offset-2"
                    href="https://safetyculture.com/checklists/5x5-risk-matrix-template"
                    rel="noopener noreferrer"
                    target="_blank"
                  />
                ),
              }}
            />
          </p>
          {/* eslint-enable jsx-a11y/anchor-has-content */}
        </div>
      </PopoverContent>
    </Popover>
  )
}

type AnalysisSectionProps = {
  requirementMatrix: RequirementMatrixItem[]
  risks: RiskItem[]
}

export function AnalysisSection({ requirementMatrix, risks }: AnalysisSectionProps) {
  const { t } = useTranslation()
  const showstoppers = risks.filter((r) => r.isShowstopper)
  const otherRisks = risks
    .filter((r) => !r.isShowstopper)
    .sort((a, b) => riskScore(b) - riskScore(a))

  const conflicts = useMemo<TenderConflict[]>(() => {
    const result: TenderConflict[] = []
    const seen = new Set<string>()

    for (const item of requirementMatrix) {
      const verConflicts = item.evidence.verification?.conflicts ?? []
      for (const c of verConflicts) {
        const key = [item.requirementId, c.withClaimId].sort().join(":")
        if (seen.has(key)) continue
        seen.add(key)

        const matchingRisk = risks.find((r) => r.id === c.withClaimId)
        result.push({
          id: key,
          claimAId: item.requirementId,
          claimALabel: item.requirement,
          claimBId: c.withClaimId,
          claimBLabel: matchingRisk?.title ?? c.withClaimId,
          description: c.description,
          severity: c.severity,
        })
      }
    }

    return result
  }, [requirementMatrix, risks])

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold">{t("tender.sections.analysis.title")}</h3>
        <p className="text-muted-foreground text-sm">{t("tender.sections.analysis.subtitle")}</p>
      </div>

      <TenderAiDisclaimer />

      {conflicts.length > 0 && <ConflictAlert conflicts={conflicts} />}

      {showstoppers.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="text-destructive size-4" />
            <h4 className="text-destructive text-sm font-semibold">
              {t("tender.sections.analysis.showstoppersHeading", { count: showstoppers.length })}
            </h4>
          </div>
          {showstoppers.map((item) => (
            <div
              key={item.id}
              className="border-destructive/30 bg-destructive/[0.03] overflow-hidden rounded-xl border"
            >
              <div className="border-destructive/30 bg-destructive/[0.05] flex items-start gap-3 border-b px-4 py-3">
                <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" />
                <p className="text-destructive text-sm font-semibold">{item.title}</p>
              </div>
              <div className="space-y-3 px-4 py-3">
                <p className="text-destructive/80 text-sm leading-relaxed">{item.description}</p>
                {item.mitigationNote && (
                  <div className="bg-destructive/[0.06] flex items-start gap-2 rounded-lg px-3 py-2">
                    <span className="text-destructive mt-0.5 text-xs font-bold">→</span>
                    <p className="text-destructive text-xs font-medium">{item.mitigationNote}</p>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  <EvidenceCell evidence={item.evidence} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">
                {t("tender.sections.analysis.kravmatris")}
              </CardTitle>
              <div className="text-muted-foreground flex items-center gap-3 text-[11px]">
                {(Object.keys(REQ_STATUS_STYLE) as ReqStatusKey[]).map((key) => {
                  const conf = REQ_STATUS_STYLE[key]
                  const Icon = conf.icon
                  const count = requirementMatrix.filter((r) => r.status === key).length
                  if (count === 0) return null
                  return (
                    <span key={key} className="flex items-center gap-1">
                      <Icon className={cn("size-3", badgeClassToTextColor(conf.badgeClass))} />
                      {count}
                    </span>
                  )
                })}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {requirementMatrix.map((item) => {
                const statusKey = item.status as ReqStatusKey
                const statusConf =
                  statusKey in REQ_STATUS_STYLE
                    ? REQ_STATUS_STYLE[statusKey]
                    : REQ_STATUS_STYLE.obekraftat
                const StatusIcon = statusConf.icon
                return (
                  <div
                    key={item.requirementId}
                    className={cn(
                      "flex items-start gap-0 border-l-2 transition-colors",
                      statusConf.borderClass,
                      statusConf.bgClass
                    )}
                  >
                    <div className="flex flex-1 items-start gap-3 px-4 py-3">
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center gap-1.5">
                          <span className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 font-mono text-[10px]">
                            {item.requirementId}
                          </span>
                        </div>
                        <p className="text-xs leading-snug">{item.requirement}</p>
                        {item.gap && (
                          <p className="text-muted-foreground text-xs italic">{item.gap}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                          <EvidenceCell evidence={item.evidence} />
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={cn(
                          "w-fit shrink-0 border text-xs font-normal",
                          statusConf.badgeClass
                        )}
                      >
                        <StatusIcon className="mr-1 size-3" />
                        {t(`tender.sections.analysis.reqStatus.${statusKey}`)}
                      </Badge>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-sm font-semibold">
                {t("tender.sections.analysis.riskmatris")}
                <RiskMatrisInfo />
              </CardTitle>
              <div className="text-muted-foreground flex items-center gap-3 text-[11px]">
                <span className="flex items-center gap-1">
                  <span className="bg-success-ink inline-block size-2 rounded-sm" />
                  {t("tender.sections.analysis.riskLevel.low")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="bg-warning inline-block size-2 rounded-sm" />
                  {t("tender.sections.analysis.riskLevel.medium")}
                </span>
                <span className="flex items-center gap-1">
                  <span className="bg-destructive inline-block size-2 rounded-sm" />
                  {t("tender.sections.analysis.riskLevel.high")}
                </span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {[...showstoppers, ...otherRisks].map((risk) => {
                const score = riskScore(risk)
                return (
                  <div
                    key={risk.id}
                    className={cn(
                      "flex items-start gap-3 px-4 py-3",
                      risk.isShowstopper && "bg-destructive/[0.02]"
                    )}
                  >
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-start gap-1.5">
                        <p className="text-xs leading-snug font-medium">{risk.title}</p>
                        {risk.isShowstopper && (
                          <Badge
                            className="border-destructive bg-destructive/10 text-destructive text-[10px]"
                            variant="outline"
                          >
                            {t("tender.sections.analysis.showstopperBadge")}
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <EvidenceCell evidence={risk.evidence} />
                      </div>
                    </div>
                    <div className="shrink-0 space-y-1 text-right">
                      <div className="text-muted-foreground flex items-center justify-end gap-1.5 text-[10px]">
                        <span>
                          {t("tender.sections.analysis.axes.severity")}:{risk.severity}
                        </span>
                        <span className="text-border">·</span>
                        <span>
                          {t("tender.sections.analysis.axes.probability")}:{risk.probability}
                        </span>
                      </div>
                      <div className="flex justify-end">
                        <RiskScoreCell probability={risk.probability} severity={risk.severity} />
                      </div>
                      <p className="text-muted-foreground text-[10px]">
                        {riskScoreLabel(score, t)}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
